/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, createContext, type ForwardedRef, forwardRef, isValidElement, type ReactElement } from 'react';
import { type ContextValue, DEFAULT_SLOT, ListBoxItem as ListBoxItemCore, type ListBoxItemProps as ListBoxItemCoreProps, Provider, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { AvatarContext } from '../avatar/avatar.component';
import { BadgeContext } from '../badge/badge.component';
import { IconContext } from '../icon/icon.component';
import { KbdContext } from '../kbd/kbd.component';
import { NumberBadgeContext } from '../number-badge/number-badge.component';
import { Text, TextContext } from '../text/text.component';

interface ListBoxItemProps<T> extends Omit<ListBoxItemCoreProps<T>, 'style' | 'className'> {
  /** The size of the menu item. (Default: 40) */
  size?: keyof typeof sizeVariants;
  /** Whether the menu item is critical. (Default: false) */
  isCritical?: boolean;
  style?: stylex.StyleXStyles;
}

const ListBoxItemContext = createContext<ContextValue<ListBoxItemProps<object>, HTMLDivElement>>({});

const ListBoxItem = forwardRef((props: ListBoxItemProps<object>, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, ListBoxItemContext);

  const { style, size = 40, isCritical = false, ...coreProps } = props;

  return (
    <ListBoxItemCore
      ref={ref}
      {...stylex.props(styles.base, sizeVariants[size], props.isCritical && criticalStyles.base, style)}
      {...coreProps}>
      {(renderProps) => {
        const resolvedChildren = typeof coreProps.children === 'function' ? coreProps.children(renderProps) : coreProps.children;

        const isTextOnly = Children.toArray(resolvedChildren).every(c => !isValidElement(c));

        return (
          <Provider values={[
            [TextContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: size === 52 ? 16 : 14,
                  style: [styles.firstRow, textStyles.base],
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    (isCritical && 'critical') ||
                    ((renderProps.isHovered || renderProps.isFocused || renderProps.isSelected) && 'primary') ||
                    'secondary'
                  ),
                  weight: 'regular',
                },
                [LIST_BOX_ITEM_SLOTS.LABEL]: {
                  size: size === 52 ? 16 : 14,
                  style: [styles.firstRow, styles.fullWidth, textStyles.base],
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    (isCritical && 'critical') ||
                    ((renderProps.isHovered || renderProps.isFocused || renderProps.isSelected) && 'primary') ||
                    'secondary'
                  ),
                  weight: renderProps.isSelected ? 'medium' : 'regular',
                },
                [LIST_BOX_ITEM_SLOTS.DESCRIPTION]: {
                  size: 12,
                  style: [styles.secondRow, textStyles.base],
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    'quaternary'
                  ),
                  weight: 'regular',
                }
              },
            }],
            [NumberBadgeContext, {
              size: 16,
              color: 'critical',
              style: styles.firstRow,
            }],
            [IconContext, {
              slots: {
                [DEFAULT_SLOT]: {
                  size: 16,
                  color: (
                    (renderProps.isDisabled && 'disabled') ||
                    (isCritical && 'critical') ||
                    ((renderProps.isHovered || renderProps.isFocused || renderProps.isSelected) && 'primary') ||
                    'secondary'
                  ),
                  style: styles.firstRow,
                },
              }
            }],
            [KbdContext, {
              style: [styles.firstRow, kbdStyles.base],
            }],
            [BadgeContext, {
              size: 20,
              isPastel: true,
              color: 'turquoise',
              style: [styles.firstRow, styles.fullWidth],
            }],
            [AvatarContext, {
              size: 40,
              style: styles.firstRow,
            }],
          ]}>
            {isTextOnly ? <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>{resolvedChildren}</Text> : resolvedChildren}
          </Provider>
        );
      }}
    </ListBoxItemCore>
  );
}) as <T>(p: ListBoxItemProps<T> & { ref?: ForwardedRef<HTMLDivElement> }) => ReactElement;

const styles = stylex.create({
  base: {
    borderRadius: 8,
    outline: 'none',
    paddingInline: 12,
    textDecoration: 'none',
    alignItems: 'center',
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered], [data-focus-visible])': $.opacity04,
    },
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'grid',
    gridColumnGap: 8,
    gridRowGap: 4,
    gridTemplateColumns: 'auto 1fr auto',
    justifyItems: 'flex-start',
  },
  firstRow: { gridArea: '1' },
  secondRow: { gridArea: '2' },
  fullWidth: { gridColumn: 'span 3' },
});

const criticalStyles = stylex.create({
  base: {
    backgroundColor: {
      default: 'transparent',
      ':is([data-hovered])': $.surfaceCriticalSilent,
    },
  }
});

const textStyles = stylex.create({
  base: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'pre',
    width: '100%',
  }
});

const kbdStyles = stylex.create({
  base: {
    marginLeft: -7,
  }
});

const sizeVariants = stylex.create({
  56: {
    paddingBlock: 8,
    height: 56,
  },
  52: {
    height: 52,
  },
  50: {
    paddingBlock: 10,
    height: 50,
  },
  48: {
    paddingBlock: 12,
    height: 48,
  },
  40: {
    paddingBlock: 6,
    height: 40,
  },
});

const LIST_BOX_ITEM_SLOTS = {
  LABEL: "label",
  DESCRIPTION: "description",
};

export { LIST_BOX_ITEM_SLOTS, ListBoxItem, ListBoxItemContext, type ListBoxItemProps };

